/**
 * What an operator is warned about before a radio changes hands.
 *
 * Attaching a radio to a site can take it out of another tenant's inventory or
 * off another site entirely, and both are consequences the operator has to
 * accept knowingly rather than discover afterwards. This module reads a radio's
 * current allocation off the radio detail response and turns it into the list of
 * warnings the change-unit dialog shows before it submits.
 *
 * Contains:
 * - deriveRadioAllocation — radio detail response → where the radio is now
 * - buildRadioSwapWarnings — an allocation + the destination → warnings to show
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Read a radio's current allocation off the radio detail response.
 *
 * The owning tenant has two sources and they are read billing first: the billed
 * site group's customer survives a return to stock, which clears the radio's
 * location row, so it is the only signal left for a radio sitting in inventory
 * on someone else's books. The location's customer covers the radio that is
 * allocated to a tenant but not billed to one.
 *
 * @param {object} radioDetail - One radio as `fetchRadio` returns it (unwrapped).
 * @returns {{
 *   radioId: ?number,
 *   iccid: ?string,
 *   macAddress: ?string,
 *   ipAddress: ?string,
 *   isOnSite: boolean,
 *   site: ?{id: number, name: string},
 *   siteGroup: ?{id: number, name: string, description: string},
 *   billedSiteGroup: ?{id: number, name: string, description: string},
 *   ownerCustomer: ?{id: number, name: string},
 *   prepaid: ?{end_date: string, owner_name: ?string},
 * }} Where the radio is now. Every reference is null when absent.
 */
export const deriveRadioAllocation = radioDetail => {
  const location = radioDetail?.location ?? null;
  const billing = radioDetail?.billing ?? null;
  const site = location?.site ?? null;
  const siteGroup = location?.site_group ?? null;
  const billedSiteGroup = billing?.billed_site_group ?? null;
  const ownerCustomer = billing?.billed_customer ?? location?.customer ?? null;

  return {
    radioId: radioDetail?.id ?? null,
    iccid: radioDetail?.iccid ?? null,
    macAddress: radioDetail?.mac_address ?? null,
    ipAddress: radioDetail?.ip_address ?? null,
    isOnSite: radioDetail?.placement_type === 'onSite',
    site: site ? { id: site.id, name: site.name } : null,
    siteGroup: siteGroup ? { id: siteGroup.id, name: siteGroup.name, description: siteGroup.description ?? '' } : null,
    billedSiteGroup: billedSiteGroup
      ? { id: billedSiteGroup.id, name: billedSiteGroup.name, description: billedSiteGroup.description ?? '' }
      : null,
    ownerCustomer: ownerCustomer ? { id: ownerCustomer.id, name: ownerCustomer.name } : null,
    // The radio detail response carries no prepaid coverage, so every prepaid
    // branch below is unreachable until it does.
    prepaid: null,
  };
};

/**
 * Build the warnings shown before a radio is attached to a site.
 *
 * @param {object} params
 * @param {object} params.allocation - The incoming radio's `deriveRadioAllocation`.
 * @param {?object} params.currentAllocation - The radio already on the site, if any.
 * @param {boolean} params.hasExistingRadio - Whether this is a swap rather than an add.
 * @param {?number} params.targetCustomerId - Customer owning the destination site.
 * @param {?string} params.targetCustomerName - That customer's display name.
 * @param {number} params.targetSiteId - The destination site's id.
 * @param {boolean} params.isStaff - Whether the operator is ODL staff.
 * @param {boolean} params.isSuperUser - Whether the operator is a superuser.
 * @param {string} params.customerTerm - The tenant's word for a customer.
 * @param {function} params.t - i18next translator.
 * @returns {Array<string|{message: string, severity: string}>} Warnings in display
 *   order — a bare string renders as a warning, an object carries its severity.
 */
export const buildRadioSwapWarnings = ({
  allocation,
  currentAllocation,
  hasExistingRadio,
  targetCustomerId,
  targetCustomerName,
  targetSiteId,
  isStaff,
  isSuperUser,
  customerTerm,
  t,
}) => {
  const warnings = [];
  if (!allocation) return warnings;

  const term = (customerTerm ?? '').toLowerCase();

  const { ownerCustomer } = allocation;
  if (isStaff && ownerCustomer?.id && targetCustomerId && ownerCustomer.id !== targetCustomerId) {
    warnings.push(
      t('sites.warningDifferentCustomer', {
        term,
        currentCustomer: ownerCustomer.name,
        targetCustomer: targetCustomerName,
        defaultValue: `Radio belongs to a different ${term}. This radio is currently in ${ownerCustomer.name} inventory. Adding the radio to this site will transfer ownership to ${targetCustomerName}.`,
      }),
    );
  }

  if (isSuperUser && allocation.isOnSite && allocation.site?.id !== targetSiteId) {
    const { site, siteGroup } = allocation;
    if (site) {
      const siteName = site.name || 'Unknown Site';
      const siteGroupName = siteGroup?.name || '';
      const siteLabel = siteGroupName ? `${siteName} (${siteGroupName})` : siteName;
      warnings.push(
        t('sites.warningRadioOnAnotherSite', {
          siteName,
          siteGroupName,
          defaultValue: `Radio exists on another site. This radio is currently on ${siteLabel}. Adding the radio to this site will remove it from the existing site and move it to this site.`,
        }),
      );
    } else {
      warnings.push(
        t('sites.warningRadioOnAnotherSiteNoDetails', {
          defaultValue:
            'Radio exists on another site. Adding the radio to this site will remove it from the existing site and move it to this site.',
        }),
      );
    }
  }

  // Prepaid coverage follows the radio, so it is only news when one radio is
  // replacing another.
  if (!hasExistingRadio) return warnings;
  const incomingPrepaid = allocation.prepaid;
  const outgoingPrepaid = currentAllocation?.prepaid;

  if (incomingPrepaid && outgoingPrepaid) {
    // The outgoing radio's fate — inventory or retire — is chosen after this
    // dialog, so both possibilities are stated.
    warnings.push({
      severity: 'info',
      message: t('sites.infoPrepaidBothInventory', {
        outEndDate: outgoingPrepaid.end_date,
      }),
    });
  } else if (incomingPrepaid) {
    warnings.push({
      severity: 'info',
      message: t('sites.infoPrepaidIncomingOnly', {
        endDate: incomingPrepaid.end_date,
        ownerName: incomingPrepaid.owner_name || t('general.unknown'),
      }),
    });
  } else if (outgoingPrepaid) {
    warnings.push({
      severity: 'info',
      message: t('sites.infoPrepaidOutgoingOnly', {
        endDate: outgoingPrepaid.end_date,
        ownerName: outgoingPrepaid.owner_name || t('general.unknown'),
      }),
    });
  }

  return warnings;
};
